import {useNavigate, useSearchParams} from "react-router-dom";
import {ttaSelector, useGetTtaByPickupAndDeliveryQuery, useGetTtaQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useEffect, useMemo, useState} from "react";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {TicketProfile} from "../../components/profile/TicketProfile/TicketProfile"
import {useTranslation} from "react-i18next";
import {EntityProfilePage} from "../EntityProfilePage/EntityProfilePage";

type Props = {
  id?: string;
  pickupId?: string
  deliveryId?: string
  database?: string
}

export const TicketProfilePageRoute = () => {
  const [params, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const id = params.get("id") ?? undefined;
  const pickupId = params.get("pid") ?? undefined
  const deliveryId = params.get("did") ?? undefined
  const database = params.get("database") ?? undefined;

  const {t} = useTranslation(["common"])

  if (!id && (!pickupId || !deliveryId)) {
    navigate(-1);
    return (
      <ErrorPage
        message={t("common.entity.ticket", "Ticket") + " " + t("common.error.notfound", "Not Found")}/>
    );
  }

  return <TicketProfilePage id={id} pickupId={pickupId} deliveryId={deliveryId} database={database}/>;
};

export const TicketProfilePage = (props: Props) => {
  const {id, pickupId, deliveryId, database} = props;

  const [access, setAccess] = useState(false);
  const [loading, setLoading] = useState(true)

  const selectTicket = useMemo(() => {
    return ttaSelector()
  }, []);

  const idQueryArg = useMemo(() => {
    return access ? (id ? {id: id, database: database} : skipToken) : skipToken
  }, [access, id, database])

  const {idTicket, isIdFetching, idError} = useGetTtaQuery(idQueryArg, {
    selectFromResult: (result) => ({
      idTicket: selectTicket(result),
      isIdFetching: result.isFetching,
      idError: result.error,
    })
  });

  const pickupDeliveryQueryArg = useMemo(() => {
    if (access && !id && pickupId && deliveryId) {
      return {
        pickupId: pickupId,
        deliveryId: deliveryId,
        database: database
      }
    } else {
      return skipToken
    }
  }, [access, id, pickupId, deliveryId, database])

  const {pdTicket, isPDFetching, pdError} = useGetTtaByPickupAndDeliveryQuery(pickupDeliveryQueryArg, {
    selectFromResult: (result) => ({
      pdTicket: selectTicket(result),
      isPDFetching: result.isFetching,
      pdError: result.error,
    })
  });

  useEffect(() => {
    setLoading(isIdFetching || isPDFetching)
  }, [isIdFetching, isPDFetching]);

  const ticket = idTicket ?? pdTicket

  return (
    <EntityProfilePage
      permissions={["transporttransportassoc_view"]}
      entity={ticket}
      isLoading={loading}
      error={!ticket && (idError ?? pdError)}
      onAccess={setAccess}
    >
      {(entity) => (
        <TicketProfile ticket={entity}/>
      )}
    </EntityProfilePage>
  )
};
